import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { categories, comics, faqItems } from "@/mocks/comics";
import ComicCard from "@/components/feature/ComicCard";

const hotComics = comics.filter((c) => c.isHot);
const newComics = comics.filter((c) => c.isNew);
const rankComics = [...comics].sort((a, b) => b.rating - a.rating).slice(0, 6);
const picks = comics.filter((c) => c.rating >= 9.0).slice(0, 4);

const categoryIntro: Record<string, string> = {
  校园: "青春日常 · 恋爱初醒",
  职场: "社畜日常 · 打工人图鉴",
  奇幻: "异界冒险 · 热血觉醒",
  恋爱: "心动信号 · 甜宠日常",
  剧情: "人生百态 · 治愈成长",
};

export default function Home() {
  usePageMeta({
    title: "羞羞漫画韩国官网 - 韩国校园、职场、奇幻、恋爱漫画在线阅读",
    description:
      "羞羞漫画韩国分类齐全，涵盖校园、职场、奇幻、恋爱、剧情等热门题材，还有实时榜单推荐，快速找到你想看的韩国漫画，满足不同口味。",
  });

  return (
    <main className="w-full bg-background-50">
      {/* ===== 首屏 Hero ===== */}
      <section className="relative overflow-hidden" aria-label="网站品牌介绍">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=stylized%20illustration%20of%20a%20towering%20pile%20of%20colorful%20Korean%20comic%20books%20with%20floating%20speech%20bubbles%20and%20stars%2C%20warm%20coral%20cream%20and%20golden%20amber%20color%20palette%2C%20soft%20gradient%20sky%2C%20dreamy%20artistic%20background%2C%20high%20contrast%20against%20dark%20text&width=1600&height=900&seq=hero-manhwa-01&orientation=landscape"
            alt="色彩缤纷的韩国漫画书籍与漂浮对话气泡插画背景"
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/60 via-background-950/45 to-background-950/60"></div>
        </div>
        <div className="relative mx-auto flex w-full max-w-7xl flex-col items-center px-4 pb-24 pt-20 text-center md:px-6 md:pb-32 md:pt-28">
          <span className="inline-flex items-center gap-2 rounded-full border border-background-50/30 bg-background-50/10 px-4 py-1.5 font-label text-sm tracking-wide text-background-50 backdrop-blur">
            <i className="ri-sparkling-2-line text-accent-400" aria-hidden="true"></i>
            MANHWA · 韩国漫画内容站
          </span>
          <h1 className="mt-6 font-display text-4xl font-black leading-tight text-background-50 md:text-6xl">
            羞羞漫画韩国
            <span className="block font-label mt-2 text-2xl text-accent-300 md:text-3xl">
              发现每一部值得阅读的韩国漫画
            </span>
          </h1>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-background-100 md:text-base">
            校园、职场、奇幻、恋爱、剧情，五大题材分类齐全；实时榜单与热门推荐，帮你快速锁定下一部想追的作品。
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link
              to="/category"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-apps-2-line text-base" aria-hidden="true"></i>
              浏览全部题材
            </Link>
            <Link
              to="/rankings"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-background-50/10 px-7 py-3 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
            >
              <i className="ri-fire-line text-base" aria-hidden="true"></i>
              查看实时榜单
            </Link>
          </div>
          <div className="mt-12 grid w-full max-w-3xl grid-cols-3 gap-3 text-background-50 md:gap-4">
            {[
              { num: "5", label: "核心题材分类" },
              { num: "120+", label: "收录人气作品" },
              { num: "1000+", label: "每日更新章节" },
            ].map((s) => (
              <div key={s.label} className="rounded-xl bg-background-50/10 px-2 py-4 backdrop-blur">
                <p className="font-display text-2xl font-black text-accent-300 md:text-3xl">{s.num}</p>
                <p className="mt-1 text-xs text-background-100">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 题材分类 ===== */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20" aria-labelledby="cat-heading">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-label text-sm tracking-widest text-primary-600">热门题材</p>
            <h2 id="cat-heading" className="mt-2 font-display text-2xl font-black text-foreground-950 md:text-3xl">
              五大分类，各取所爱
            </h2>
          </div>
          <Link
            to="/category"
            className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
          >
            查看全部 <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {categories.map((c, i) => (
            <Link
              key={c}
              to={`/category?type=${encodeURIComponent(c)}`}
              className="group relative flex h-44 flex-col justify-end overflow-hidden rounded-xl p-4 transition-transform duration-300 hover:-translate-y-1"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, oklch(var(--primary-100)), oklch(var(--accent-100)))",
              }}
            >
              <span className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-lg bg-background-50/70 text-primary-600 backdrop-blur">
                <i className="ri-arrow-right-up-line text-lg" aria-hidden="true"></i>
              </span>
              <span className="font-display text-xl font-black text-primary-700">{c}</span>
              <p className="mt-1 text-xs text-primary-700/80">{categoryIntro[c]}</p>
              <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary-800">
                <i className="ri-book-2-line" aria-hidden="true"></i>
                {i + 3} 部代表作品
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== 热门推荐 ===== */}
      <section className="bg-background-100 py-14 md:py-20" aria-labelledby="hot-heading">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-label text-sm tracking-widest text-primary-600">编辑精选</p>
              <h2 id="hot-heading" className="mt-2 font-display text-2xl font-black text-foreground-950 md:text-3xl">
                热门推荐 · 人气之作
              </h2>
            </div>
            <Link
              to="/rankings"
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              前往榜单 <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {hotComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== 新作速递 ===== */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20" aria-labelledby="new-heading">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-label text-sm tracking-widest text-accent-600">本周新作</p>
            <h2 id="new-heading" className="mt-2 font-display text-2xl font-black text-foreground-950 md:text-3xl">
              新作速递 · 抢先入坑
            </h2>
          </div>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {newComics.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </section>

      {/* ===== 实时榜单 + 精选 ===== */}
      <section className="bg-background-100 py-14 md:py-20" aria-labelledby="rank-heading">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <p className="font-label text-sm tracking-widest text-primary-600">数据热榜</p>
              <h2 id="rank-heading" className="mt-2 font-display text-2xl font-black text-foreground-950 md:text-3xl">
                实时榜单 Top 6
              </h2>
              <ol className="mt-8 space-y-3">
                {rankComics.map((comic, i) => (
                  <li key={comic.id}>
                    <Link
                      to={`/detail/${comic.id}`}
                      className="group flex items-center gap-4 rounded-lg bg-background-50 p-2.5 ring-1 ring-background-200/70 transition-all hover:ring-primary-300"
                    >
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md font-display text-lg font-black ${
                          i === 0
                            ? "bg-accent-500 text-background-50"
                            : "bg-secondary-100 text-secondary-900"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <img
                        src={comic.cover}
                        alt=""
                        className="h-16 w-12 shrink-0 rounded-md object-cover object-top"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-display text-sm font-bold text-foreground-950 group-hover:text-primary-600">
                          {comic.title}
                        </p>
                        <p className="mt-0.5 flex items-center gap-2 text-xs text-foreground-500">
                          <span>{comic.category}</span>
                          <span className="flex items-center gap-1 text-accent-600">
                            <i className="ri-star-fill" aria-hidden="true"></i>
                            {comic.rating.toFixed(1)}
                          </span>
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="lg:col-span-3">
              <p className="font-label text-sm tracking-widest text-accent-600">编辑力荐</p>
              <h2 className="mt-2 font-display text-2xl font-black text-foreground-950 md:text-3xl">
                高分佳作 · 口碑之选
              </h2>
              <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {picks.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/detail/${comic.id}`}
                    className="group flex gap-4 rounded-xl bg-background-50 p-3 ring-1 ring-background-200/70 transition-all hover:ring-primary-300"
                  >
                    <img
                      src={comic.cover}
                      alt={`${comic.title} 封面`}
                      title={`${comic.title} · 韩国${comic.category}漫画`}
                      className="h-32 w-24 shrink-0 rounded-lg object-cover object-top"
                    />
                    <div className="flex min-w-0 flex-col">
                      <span className="inline-flex w-fit items-center gap-1 rounded-md bg-accent-100 px-2 py-0.5 text-[11px] font-medium text-accent-900">
                        <i className="ri-star-fill text-xs" aria-hidden="true"></i>
                        {comic.rating.toFixed(1)} 高分
                      </span>
                      <p className="mt-2 truncate font-display text-base font-bold text-foreground-950 group-hover:text-primary-600">
                        {comic.title}
                      </p>
                      <p className="mt-1.5 line-clamp-3 text-xs leading-relaxed text-foreground-600">
                        {comic.intro}
                      </p>
                      <div className="mt-auto flex items-center gap-3 text-xs text-foreground-400">
                        <span>{comic.chapters}话</span>
                        <span>{comic.status}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 品牌口碑 ===== */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20" aria-labelledby="about-heading">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div className="relative overflow-hidden rounded-2xl">
            <img
              src="https://readdy.ai/api/search-image?query=cozy%20illustration%20of%20readers%20relaxing%20with%20open%20comic%20books%20on%20a%20warm%20couch%20with%20soft%20lamplight%2C%20warm%20coral%20cream%20and%20amber%20palette%2C%20comfortable%20storytelling%20atmosphere%2C%20stylized%20artistic%20illustration&width=1200&height=800&seq=about-reader-01&orientation=landscape"
              alt="读者们温暖舒适地阅读漫画的插画"
              className="h-full w-full object-cover"
            />
          </div>
          <div>
            <p className="font-label text-sm tracking-widest text-primary-600">关于我们</p>
            <h2 id="about-heading" className="mt-2 font-display text-2xl font-black text-foreground-950 md:text-3xl">
              为每一位漫友，做一扇通往韩国漫画的门
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-foreground-600">
              我们相信，好的故事值得被更多人看见。羞羞漫画韩国专注于整理与推荐优质的韩国漫画作品，通过清晰的分区与客观的榜单，让每一位读者都能快速找到契合自己口味的连载。
            </p>
            <ul className="mt-6 space-y-3">
              {[
                { icon: "ri-file-list-3-line", title: "真实收录", desc: "收录范围持续扩充，热门题材与冷门佳作兼收。" },
                { icon: "ri-line-chart-line", title: "榜单客观", desc: "基于综合数据排序，热度与口碑一目了然。" },
                { icon: "ri-heart-3-line", title: "阅读友好", desc: "卡片式信息流，更新状态、章节与评分清晰可见。" },
              ].map((f) => (
                <li key={f.title} className="flex items-start gap-3 rounded-lg bg-background-100 p-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-lg text-primary-600">
                    <i className={f.icon} aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{f.title}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-foreground-600">{f.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
            <Link
              to="/about"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              了解更多 <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="bg-background-100 py-14 md:py-20" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <p className="text-center font-label text-sm tracking-widest text-primary-600">常见问题</p>
          <h2 id="faq-heading" className="mt-2 text-center font-display text-2xl font-black text-foreground-950 md:text-3xl">
            你可能想知道的
          </h2>
          <div className="mt-8 space-y-3">
            {faqItems.slice(0, 3).map((item) => (
              <details
                key={item.q}
                className="group rounded-xl bg-background-50 p-5 ring-1 ring-background-200/70"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold text-foreground-950">
                  {item.q}
                  <i
                    className="ri-add-line text-lg text-primary-500 transition-transform duration-300 group-open:rotate-45"
                    aria-hidden="true"
                  ></i>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link
              to="/faq"
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              查看更多问题 <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="relative overflow-hidden rounded-2xl bg-background-950 px-6 py-12 text-center md:px-12 md:py-16">
          <div className="absolute inset-0 opacity-30">
            <img
              src="https://readdy.ai/api/search-image?query=wide%20dark%20stylized%20background%20of%20floating%20comic%20speech%20bubbles%20and%20stars%20on%20deep%20ink%20with%20coral%20and%20amber%20accents%2C%20abstract%20artistic%20illustration&width=1600&height=500&seq=cta-comic-01&orientation=landscape"
              alt=""
              className="h-full w-full object-cover"
            />
          </div>
          <div className="relative mx-auto max-w-2xl">
            <h2 className="font-display text-2xl font-black text-background-50 md:text-3xl">
              还没想好看什么？榜单帮你做决定
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-background-200">
              从校园青春到奇幻冒险，从职场日常到甜蜜恋爱，实时更新的韩国漫画榜单，让每一部好作品都不被错过。
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Link
                to="/rankings"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-fire-line text-base" aria-hidden="true"></i>
                查看人气榜单
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
              >
                去分类逛逛
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQPage 结构化数据 */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
    </main>
  );
}